import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  FolderOpen,
  Building2,
  Users,
  Key,
  Server,
  Globe,
  Shield,
  FileKey,
  Package,
  Network,
  File,
  Paperclip,
  Plus,
  Search,
  AlertCircle,
  TrendingUp,
  ArrowLeft
} from 'lucide-react';
import OrganizationsModule from './OrganizationsModule';
import ContactsModule from './ContactsModule';
import PasswordVaultModule from './PasswordVaultModule';
import DevicesModule from './DevicesModule';
import DomainsModule from './DomainsModule';
import SSLCertificatesModule from './SSLCertificatesModule';
import LicensesModule from './LicensesModule';
import VendorsModule from './VendorsModule';
import DocumentsModule from './DocumentsModule';
import NetworkDiagramsModule from './NetworkDiagramsModule';
import ActivityLogsModule from './ActivityLogsModule';
import SitesModule from './SitesModule';

import { BACKEND_URL } from '@/lib/backend';

// Router wrapper: gives child Modules the flex/overflow context they expect
// (they use `flex-1 overflow-y-auto` internally and need a parent that
// provides a constrained height + flex-column layout).
function SubViewShell({ onBack, children }) {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="shrink-0 bg-slate-50 border-b border-slate-200">
        <Button variant="ghost" onClick={onBack} className="ml-2 my-2" data-testid="doc-back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
        </Button>
      </div>
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {children}
      </div>
    </div>
  );
}

export default function DocumentationModule() {
  const [stats, setStats] = useState({
    organizations: 0,
    passwords: 0,
    devices: 0,
    domains: 0,
    expiringDomains: 0,
    sslCertificates: 0,
    expiringSsl: 0,
    licenses: 0,
    contacts: 0,
    documents: 0
  });

  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard'); // dashboard, organizations, contacts, etc.

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      // Load real stats from API (organizations, passwords, devices, ...).
      const response = await fetch(`${BACKEND_URL}/api/documentation/analytics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const base = response.ok ? await response.json() : {};

      // Sites aren't part of the doc analytics endpoint — fetch the list
      // directly to derive a count so the card can surface a live number.
      let sitesCount = 0;
      try {
        const sitesResp = await fetch(`${BACKEND_URL}/api/sites`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (sitesResp.ok) {
          const arr = await sitesResp.json();
          sitesCount = Array.isArray(arr) ? arr.length : 0;
        }
      } catch (e) {
        // Non-fatal — leave at 0.
      }

      setStats({ ...base, sites: sitesCount });
    } catch (err) {
      console.error('Failed to load documentation stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Sites',
      value: stats.sites || 0,
      icon: Building2,
      color: 'bg-cyan-100 text-cyan-700',
      description: 'Documented locations — click a site to edit contacts, AV, backups, and notes',
      primary: true,
    },
    {
      title: 'Organizations',
      value: stats.organizations,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      description: 'One per customer (legacy view)',
    },
    {
      title: 'Passwords',
      value: stats.passwords,
      icon: Key,
      color: 'bg-purple-100 text-purple-600',
      description: 'Secure credentials'
    },
    {
      title: 'Devices',
      value: stats.devices,
      icon: Server,
      color: 'bg-cyan-100 text-cyan-600',
      description: 'Tracked hardware'
    },
    {
      title: 'Domains',
      value: stats.domains,
      icon: Globe,
      color: 'bg-green-100 text-green-600',
      description: 'Registered domains',
      alert: stats.expiringDomains > 0 ? `${stats.expiringDomains} expiring soon` : null
    },
    {
      title: 'SSL Certificates',
      value: stats.sslCertificates,
      icon: Shield,
      color: 'bg-teal-100 text-teal-600',
      description: 'Active certificates',
      alert: stats.expiringSsl > 0 ? `${stats.expiringSsl} expiring soon` : null
    },
    {
      title: 'Licenses',
      value: stats.licenses,
      icon: FileKey,
      color: 'bg-amber-100 text-amber-600',
      description: 'Software licenses'
    },
    {
      title: 'Contacts',
      value: stats.contacts,
      icon: Users,
      color: 'bg-pink-100 text-pink-600',
      description: 'Organization contacts'
    },
    {
      title: 'Documents',
      value: stats.documents,
      icon: File,
      color: 'bg-indigo-100 text-indigo-600',
      description: 'SOPs & guides'
    }
  ];

  // Render different views — SubViewShell gives child modules a properly
  // flex-sized parent so their internal `flex-1 overflow-y-auto` works.
  const SUB_VIEWS = {
    sites: SitesModule,
    organizations: OrganizationsModule,
    contacts: ContactsModule,
    passwords: PasswordVaultModule,
    devices: DevicesModule,
    domains: DomainsModule,
    ssl: SSLCertificatesModule,
    licenses: LicensesModule,
    vendors: VendorsModule,
    documents: DocumentsModule,
    'network-diagrams': NetworkDiagramsModule,
    'activity-logs': ActivityLogsModule,
  };
  const SubComponent = SUB_VIEWS[currentView];
  if (SubComponent) {
    return (
      <SubViewShell onBack={() => setCurrentView('dashboard')}>
        <SubComponent />
      </SubViewShell>
    );
  }

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800">
              IT Documentation
            </h1>
            <p className="text-sm md:text-base text-slate-500 mt-1">
              Each site is a complete documentation unit — click a site to add contacts, AV, backups, and notes.
            </p>
          </div>
          <Button 
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            onClick={() => setCurrentView('sites')}
            data-testid="doc-quick-add-site"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add / Edit Site
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="mb-6 border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Search passwords, devices, domains, contacts, documents..."
              className="pl-10 bg-white border-slate-300"
            />
          </div>
        </CardContent>
      </Card>

      {/* Alerts Section */}
      {(stats.expiringDomains > 0 || stats.expiringSsl > 0) && (
        <div className="mb-6 space-y-3">
          {stats.expiringDomains > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-orange-900">
                      {stats.expiringDomains} Domain{stats.expiringDomains > 1 ? 's' : ''} Expiring Soon
                    </p>
                    <p className="text-sm text-orange-700">
                      Review and renew expiring domains to avoid service interruption
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto text-orange-600 border-orange-300 hover:bg-orange-100">
                    Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {stats.expiringSsl > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-900">
                      {stats.expiringSsl} SSL Certificate{stats.expiringSsl > 1 ? 's' : ''} Expiring Soon
                    </p>
                    <p className="text-sm text-red-700">
                      Renew SSL certificates to maintain secure connections
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto text-red-600 border-red-300 hover:bg-red-100">
                    Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => {
              if (stat.title === 'Sites') setCurrentView('sites');
              if (stat.title === 'Organizations') setCurrentView('organizations');
              if (stat.title === 'Passwords') setCurrentView('passwords');
              if (stat.title === 'Devices') setCurrentView('devices');
              if (stat.title === 'Domains') setCurrentView('domains');
              if (stat.title === 'SSL Certificates') setCurrentView('ssl');
              if (stat.title === 'Licenses') setCurrentView('licenses');
              if (stat.title === 'Contacts') setCurrentView('contacts');
            }}
          >
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6" />
                </div>
                {stat.alert && (
                  <Badge variant="destructive" className="text-xs">
                    {stat.alert}
                  </Badge>
                )}
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-1">{stat.value}</h3>
              <p className="text-sm font-medium text-slate-600">{stat.title}</p>
              <p className="text-xs text-slate-500 mt-1">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="text-lg">Quick Add</CardTitle>
            <CardDescription>Create new documentation entries</CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                onClick={() => setCurrentView('organizations')}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Organization
              </Button>
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                onClick={() => setCurrentView('passwords')}
              >
                <Key className="w-4 h-4 mr-2" />
                Password
              </Button>
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                onClick={() => setCurrentView('devices')}
              >
                <Server className="w-4 h-4 mr-2" />
                Device
              </Button>
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                onClick={() => setCurrentView('domains')}
              >
                <Globe className="w-4 h-4 mr-2" />
                Domain
              </Button>
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                onClick={() => setCurrentView('ssl')}
              >
                <Shield className="w-4 h-4 mr-2" />
                SSL Cert
              </Button>
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                onClick={() => setCurrentView('licenses')}
              >
                <FileKey className="w-4 h-4 mr-2" />
                License
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="text-lg">Recent Activity</CardTitle>
            <CardDescription>Latest documentation updates</CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-2 rounded hover:bg-slate-50">
                <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
                  <Key className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    Password added for Acme Corp - Office 365
                  </p>
                  <p className="text-xs text-slate-500">2 hours ago</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-2 rounded hover:bg-slate-50">
                <div className="w-8 h-8 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center flex-shrink-0">
                  <Server className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    Device updated: Main Server - warranty extended
                  </p>
                  <p className="text-xs text-slate-500">5 hours ago</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-2 rounded hover:bg-slate-50">
                <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    Domain renewed: example.com
                  </p>
                  <p className="text-xs text-slate-500">1 day ago</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coming Soon - Submodules */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-600" />
            Documentation Modules
          </CardTitle>
          <CardDescription>
            Comprehensive IT documentation and asset management
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Organizations', icon: Building2, status: 'Active', view: 'organizations' },
              { name: 'Contacts', icon: Users, status: 'Active', view: 'contacts' },
              { name: 'Password Vault', icon: Key, status: 'Active', view: 'passwords' },
              { name: 'Devices', icon: Server, status: 'Active', view: 'devices' },
              { name: 'Domains', icon: Globe, status: 'Active', view: 'domains' },
              { name: 'SSL Certificates', icon: Shield, status: 'Active', view: 'ssl' },
              { name: 'Licenses', icon: FileKey, status: 'Active', view: 'licenses' },
              { name: 'Vendors', icon: Package, status: 'Active', view: 'vendors' },
              { name: 'Documents', icon: File, status: 'Active', view: 'documents' },
              { name: 'Network Diagrams', icon: Network, status: 'Active', view: 'network-diagrams' },
              { name: 'Activity Logs', icon: FolderOpen, status: 'Active', view: 'activity-logs' }
            ].map((module, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-cyan-300 hover:bg-cyan-50/50 transition-colors ${module.view ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'}`}
                onClick={() => module.view && setCurrentView(module.view)}
              >
                <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                  <module.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{module.name}</p>
                  <p className="text-xs text-slate-500">{module.status}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
